import type {
  AiAccountMonthlyCostResponse,
  AiCostsByAccountResponse,
  AiCostsByFeatureResponse,
  AiCostsByModelResponse,
  AiCostsByUsageTypeResponse,
} from "@nlc/shared-types";

import { MBC_AI_API_URL } from "@/features/request/mbc-review/config";

import type { AiCostsRange } from "../aiCostsSearch";

const USAGE_URL = `${MBC_AI_API_URL}/usage`;

export async function aiRequest<T>(
  url: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(url, init);

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;

    throw new Error(body?.error ?? `HTTP ${response.status}`);
  }

  return (await response.json()) as T;
}

function usageRequest<T>(path: string): Promise<T> {
  return aiRequest(`${USAGE_URL}${path}`);
}

function rangeQuery({ from, to }: AiCostsRange): string {
  return new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
  }).toString();
}

export function getAiCostsByUsageType(
  range: AiCostsRange,
): Promise<AiCostsByUsageTypeResponse> {
  return usageRequest(`/costs/by-usage-type?${rangeQuery(range)}`);
}

export function getAiCostsByAccount(
  range: AiCostsRange,
): Promise<AiCostsByAccountResponse> {
  return usageRequest(`/costs/by-account?${rangeQuery(range)}`);
}

export function getAiCostsByModel(
  range: AiCostsRange,
): Promise<AiCostsByModelResponse> {
  return usageRequest(`/costs/by-model?${rangeQuery(range)}`);
}

export function getAiCostsByFeature(
  range: AiCostsRange,
): Promise<AiCostsByFeatureResponse> {
  return usageRequest(`/costs/by-feature?${rangeQuery(range)}`);
}

export function getAccountMonthlyAiCost(
  accountId: number,
  month: string,
): Promise<AiAccountMonthlyCostResponse> {
  return usageRequest(
    `/accounts/${accountId}/monthly-cost?${new URLSearchParams({ month }).toString()}`,
  );
}
